<template>
  <div>
    <v-list dense>
      <v-subheader>{{ $t('resolutions') }}</v-subheader>
      <v-list-item v-for="(action, idx) in boardItem.report.resolutions" :key="`resolution-${idx}`">
        <v-list-item-action>
          <v-icon small v-text="stateIcon(action.status)" />
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            <v-list-item-title>{{ action.display }}</v-list-item-title>
            <v-list-item-subtitle>{{ action.transition }}</v-list-item-subtitle>
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action v-if="canEdit">
          <v-menu bottom left v-if="action.status !== 'DONE'">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon small class="mr-2">
                <v-icon small v-text="$vuetify.icons.values.options" />
              </v-btn>
            </template>
            <v-list>
              <v-list-item>
                <v-btn small text @click="resolve(action)">
                  {{ $t('resolve') }}
                </v-btn>
              </v-list-item>
              <v-list-item>
                <v-btn small text @click="dismiss(action)">
                  {{ $t('dismiss') }}
                </v-btn>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item-action>
      </v-list-item>
      <v-subheader>{{ $t('lastTransitions') }}</v-subheader>
      <v-data-table
        :headers="headers"
        :items="transitions"
        :items-per-page="rowsPerPage"
        :sort-by="sortBy"
        sort-desc
        hide-default-header
        hide-default-footer
        show-expand
        single-expand
        item-key="date"
        class="flow-log"
      >
        <template v-slot:item.icon="{}">
          <v-icon small v-text="$vuetify.icons.values.flows" />
        </template>
        <template v-slot:item.date="{ item }">
          {{ transformDate(item.date) }}
        </template>
        <template v-slot:item.warning="{ item }">
          <v-icon small v-text="$vuetify.icons.values.warning" v-if="item.hasTransitionWarning" />
        </template>
        <template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
          <v-icon
            small
            v-text="isExpanded ? $vuetify.icons.values.collapse : $vuetify.icons.values.expand"
            v-if="item.transition.actions.length > 0"
            @click="expand(!isExpanded)"
          />
        </template>

        <template v-slot:expanded-item="{ item }">
          <td :colspan="headers.length" class="pa-0">
            <v-list dense tile v-if="item.transition.actions.length > 0">
              <v-list-item v-for="action in item.transition.actions" :key="action.id" @click="doNothing">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ action.display }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon small v-text="stateIcon(action.status)" />
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </td>
        </template>
      </v-data-table>
    </v-list>
  </div>
</template>

<script>
import { orderBy } from 'lodash/collection'
import { mapState, mapActions, mapGetters } from 'vuex'
import { formatDT } from '@argon/app/helpers'
import entityPerimeter from '../perimeters'
import { NAMESPACE } from '../store'
import { NAMESPACE as ME_NAMESPACE } from '@argon/iam/me/store'

export default {
  name: 'FlowLog',
  perimeters: [entityPerimeter],
  data: () => ({
    headers: [
      { text: '', value: 'icon', sortable: false, width: 30 },
      { text: '', value: 'display', sortable: false },
      { text: '', value: 'date', align: 'right', sortable: false },
      { text: '', value: 'warning', align: 'right', sortable: false, width: 30 },
      { text: '', value: 'data-table-expand', align: 'right', sortable: false, width: 20 }
    ],
    sortBy: 'date',
    rowsPerPage: -1
  }),
  computed: {
    ...mapState(NAMESPACE, ['boardItem']),
    ...mapGetters(ME_NAMESPACE, ['timezone']),
    entity() {
      return this.boardItem.entity
    },
    report() {
      return this.boardItem.report
    },
    transitions() {
      return this.report.transitions
        ? this.report.transitions.map((item) => ({
            ...item,
            hasTransitionWarning: item.status !== 'DONE'
          }))
        : []
    },
    canEdit() {
      return this.$entity.isAllowed('update', this.boardItem.entity)
    }
  },
  methods: {
    ...mapActions(NAMESPACE, ['resolveAction', 'dismissAction']),
    transformDate(value) {
      return value ? formatDT(value, { format: 'LLL', tz: this.timezone }) : ''
    },
    resolve(action) {
      this.resolveAction({
        boardRef: this.boardItem.board_refname,
        entityId: this.boardItem.entity.id,
        actionId: action.id
      })
    },
    dismiss(action) {
      this.dismissAction({
        boardRef: this.boardItem.board_refname,
        entityId: this.boardItem.entity.id,
        actionId: action.id
      })
    },
    doNothing() {},
    stateIcon(status) {
      switch (status) {
        case 'PENDING':
          return this.$vuetify.icons.values.clock
        case 'FAILED':
          return this.$vuetify.icons.values.warning
        default:
          return this.$vuetify.icons.values.check
      }
    }
  }
}
</script>

<style scoped lang="scss">
.icon {
  width: 16px;
  text-align: left;
}

.datetime {
  width: 150px;
  text-align: right;
}
</style>

<i18n>
{
  "en": {
    "dismiss": "Abandon",
    "lastTransitions": "Last Transitions",
    "resolutions": "Last Errors",
    "resolve": "Try Again"
  },
  "fr": {
    "dismiss": "Abandonner",
    "lastTransitions": "Dernières transitions",
    "resolutions": "Dernières erreurs",
    "resolve": "Réessayer"
  }
}
</i18n>
